import posthog from 'posthog-js'
import type { ConsultationFlowVariant } from './consultationExperiment'

// ─── Helpers ─────────────────────────────────────────────────

/** Capture UTM params from the current URL and return as a flat object */
function getUtmProperties(): Record<string, string> {
  if (typeof window === 'undefined') return {}
  const params = new URLSearchParams(window.location.search)
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']
  const result: Record<string, string> = {}
  for (const key of utmKeys) {
    const value = params.get(key)
    if (value) result[key] = value
  }
  return result
}

/**
 * Fire a "lead conversion" pageview for a dedicated thank-you URL.
 *
 * Sends an explicit GA4 `page_view` (gtag only auto-tracks the initial load,
 * not client-side URL changes) and a PostHog `$pageview`. This lets the
 * thank-you URLs be used as destination-URL / pageview-based conversions in
 * Google Ads / GA4 and PostHog without disturbing existing form events.
 *
 * Note: this only fires the analytics events — update the address bar
 * separately (e.g. via `window.history.replaceState`) at the call site.
 */
export function trackLeadConversionPageview(props: { path: string; title?: string }) {
  if (typeof window === 'undefined') return

  const url = window.location.origin + props.path

  const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag
  if (typeof gtag === 'function') {
    gtag('event', 'page_view', {
      page_path: props.path,
      page_location: url,
      page_title: props.title ?? document.title,
    })
  }

  posthog.capture('$pageview', { $current_url: url })
}

// ─── Core Conversion Events ─────────────────────────────────

export function trackConsultationFormSubmitted(props: {
  consultation_type: 'free' | 'paid'
  persona_segment?: string
  referral_source?: string
  form_flow_variant?: ConsultationFlowVariant
}) {
  posthog.capture('consultation_form_submitted', {
    ...props,
    ...getUtmProperties(),
    page_url: window.location.pathname,
  })
}

export function trackContactFormSubmitted(props: { inquiry_type?: string }) {
  posthog.capture('contact_form_submitted', {
    ...props,
    ...getUtmProperties(),
    page_url: window.location.pathname,
  })
}

export function trackPhoneCallInitiated() {
  posthog.capture('phone_call_initiated', {
    page_url: window.location.pathname,
    device_type: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
  })
}

export function trackEmailLinkClicked() {
  posthog.capture('email_link_clicked', {
    page_url: window.location.pathname,
  })
}

// ─── Engagement & Navigation Events ─────────────────────────

export function trackPersonaCardClicked(personaType: string) {
  posthog.capture('persona_card_clicked', {
    persona_type: personaType,
    page_url: window.location.pathname,
  })
}

export function trackVisaPageViewed(visaType: string) {
  posthog.capture('visa_page_viewed', {
    visa_type: visaType,
    ...getUtmProperties(),
  })
}

export function trackArticleViewed(props: {
  article_title: string
  article_type: 'article' | 'visa_guide'
  author?: string
}) {
  posthog.capture('article_viewed', {
    ...props,
    ...getUtmProperties(),
  })
}

export function trackCtaClicked(props: {
  cta_text: string
  cta_location:
    | 'hero'
    | 'nav'
    | 'footer'
    | 'mid_page'
    | 'testimonials'
    | 'eligibility_section'
    | 'eligibility_results'
    | 'eligibility_intro'
    | 'guide_detail'
    | 'guides_list'
    | 'personas_list'
    | 'tools_list'
    | 'article_inline'
    | 'article_end'
    | 'team_link_page'
    | 'videos_list'
    | 'video_detail'
    | 'event_offer'
    | '404_page'
  destination_url: string
}) {
  posthog.capture('cta_clicked', {
    ...props,
    page_url: window.location.pathname,
  })
}

export function trackTestimonialScrolled(props: {
  testimonial_index: number
  direction: 'next' | 'previous'
}) {
  posthog.capture('testimonial_scrolled', {
    ...props,
    page_url: window.location.pathname,
  })
}

export function trackResourceCenterViewed() {
  posthog.capture('resource_center_viewed', {
    entry_source: document.referrer || '$direct',
  })
}

// ─── Consultation Form Funnel Events ────────────────────────

export function trackConsultationFormStarted(props: {
  consultation_type?: 'free' | 'paid'
  entry_page: string
  form_flow_variant?: ConsultationFlowVariant
}) {
  posthog.capture('consultation_form_started', {
    ...props,
    ...getUtmProperties(),
  })
}

export function trackConsultationFormStepCompleted(props: {
  step_number: number
  step_name: string
  consultation_type?: 'free' | 'paid'
  form_flow_variant?: ConsultationFlowVariant
}) {
  posthog.capture('consultation_form_step_completed', props)
}

export function trackConsultationFormAbandoned(props: {
  last_step_completed: number
  consultation_type?: 'free' | 'paid'
  time_spent_seconds: number
  form_flow_variant?: ConsultationFlowVariant
}) {
  posthog.capture('consultation_form_abandoned', props)
}

// ─── Newsletter Events ──────────────────────────────────────

export function trackNewsletterSubscribed() {
  posthog.capture('newsletter_subscribed', {
    page_url: window.location.pathname,
    ...getUtmProperties(),
  })
}
